import React, { useRef, useState } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { Button } from "../../Button";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";
import {
  uploadSpecialPartnerReport,
  SpecialPartnerViewModel,
  UploadReportResult,
  UserIdType,
} from "../../../api/specialPartners";

const detectUserIdType = (userId: string): UserIdType =>
  userId.includes("@") ? "Email" : "OisterUserId";

interface UploadReportModalProps {
  isOpen: boolean;
  partners: SpecialPartnerViewModel[];
  onClose: () => void;
}

interface CsvRow {
  userId: string;
  amount: string; // empty string if partner has fixedAmount
  transactionDate: string;
}

const parseAmount = (value: string): number | null => {
  const normalized = value.replace(",", ".");
  const num = parseFloat(normalized);
  return isNaN(num) ? null : num;
};

const getTodayDateString = (): string => {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`;
};

const HEADER_KEYWORDS = ["userid", "user_id", "id", "email", "user id"];

const parseCsv = (text: string): CsvRow[] => {
  const lines = text.trim().split(/\r?\n/);
  if (lines.length === 0) return [];
  const firstParts = lines[0].split(",").map((p) => p.trim().replace(/^"|"$/g, ""));
  const isHeader = HEADER_KEYWORDS.includes(firstParts[0].toLowerCase());
  const dataLines = isHeader ? lines.slice(1) : lines;
  if (dataLines.length === 0) return [];
  return dataLines.map((line) => {
    const parts = line.split(",").map((p) => p.trim().replace(/^"|"$/g, ""));
    return { userId: parts[0] ?? "", amount: parts[1] ?? "", transactionDate: parts[2] ?? "" };
  });
};

const formatCashback = (
  partner: SpecialPartnerViewModel,
  field: "totalCashback" | "userCashback"
) => {
  const value = partner[field];
  return partner.cashbackType === "Percentage" ? `${value}%` : `${value} DKK`;
};

const calcCashback = (
  partner: SpecialPartnerViewModel,
  amount: number,
  field: "totalCashback" | "userCashback"
) => {
  const rate = partner[field];
  if (partner.cashbackType === "Fixed") return `${rate} DKK`;
  return `${(amount * rate / 100).toFixed(2)} DKK`;
};

const UploadReportModal: React.FC<UploadReportModalProps> = ({
  isOpen,
  partners,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedPartner, setSelectedPartner] = useState<SpecialPartnerViewModel | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [rows, setRows] = useState<CsvRow[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState<UploadReportResult | null>(null);

  const handleClose = () => {
    if (isSubmitting) return;
    setSelectedPartner(null);
    setRows([]);
    setFileName(null);
    setCsvFile(null);
    setParseError(null);
    setResult(null);
    onClose();
  };

  const handleFile = (file: File) => {
    if (!file.name.endsWith(".csv")) {
      setParseError("Only CSV files are supported.");
      return;
    }
    setFileName(file.name);
    setCsvFile(file);
    setParseError(null);
    setRows([]);
    setResult(null);
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const parsed = parseCsv(text);
      if (parsed.length === 0) {
        setParseError("No data rows found. Make sure the CSV has at least one row.");
        return;
      }
      setRows(parsed);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const hasFixedAmount = !!(selectedPartner?.fixedAmount);
  const invalidRows = rows.filter(
    (r) => !r.userId || (!hasFixedAmount && parseAmount(r.amount) === null)
  );
  const canUpload = !!selectedPartner && !!csvFile && rows.length > 0 && invalidRows.length === 0;

  const handleSubmit = async () => {
    if (!selectedPartner || !csvFile) return;
    setIsSubmitting(true);
    try {
      const today = getTodayDateString();
      const csvContent = [
        "userId,amount,transactionDate",
        ...rows.map((r) => `${r.userId},${r.amount},${r.transactionDate || today}`),
      ].join("\n");
      const fileToUpload = new File([csvContent], csvFile.name, { type: "text/csv" });
      const uploadResult = await uploadSpecialPartnerReport(
        selectedPartner.id,
        detectUserIdType(rows[0]?.userId ?? ""),
        fileToUpload,
      );
      setResult(uploadResult);
    } catch (err: any) {
      setParseError(err?.response?.data?.message || "Upload failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPartnerOption = (p: SpecialPartnerViewModel) => {
    const cashback = p.cashbackType === "Percentage"
      ? `${p.totalCashback}% / ${p.userCashback}%`
      : `${p.totalCashback} DKK / ${p.userCashback} DKK`;
    const fixed = p.fixedAmount ? ` · fixed ${p.fixedAmount} DKK` : "";
    return `${p.campaignName} — ${p.cashbackType} ${cashback}${fixed}`;
  };

  const partnerOptions = partners.map((p) => ({ value: p.id, label: formatPartnerOption(p) }));

  return (
    <Modal
      className="modal-content md:max-w-[680px] max-h-[90vh] overflow-y-auto"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Upload Report"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Upload Report</h2>
        <Button onClick={handleClose} variant="icon" disabled={isSubmitting}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      {result ? (
        // ── Results ───────────────────────────────────────────────────────────
        <div className="space-y-4">
          <div className={`px-4 py-4 rounded-lg border ${
            result.failedCount === 0
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-orange-50 border-orange-200 text-orange-800"
          }`}>
            <p className="font-medium mb-1">{selectedPartner?.campaignName}</p>
            <p className="text-sm">
              {result.successCount} of {result.totalRows} transactions uploaded successfully.
              {result.failedCount > 0 && ` ${result.failedCount} failed.`}
            </p>
          </div>

          {result.failedCount > 0 && (
            <div className="overflow-x-auto max-h-48 overflow-y-auto border border-gray-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 text-gray-600">User ID</th>
                    <th className="text-left px-3 py-2 text-gray-600">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.filter((r) => !r.success).map((r, i) => (
                    <tr key={i} className="bg-red-50">
                      <td className="px-3 py-1.5 text-gray-700">{r.userId}</td>
                      <td className="px-3 py-1.5 text-red-600">{r.errorMessage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={handleClose}>Close</Button>
          </div>
        </div>
      ) : (
        // ── Upload form ───────────────────────────────────────────────────────
        <div className="space-y-5">

          {/* Partner selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campaign
            </label>
            <Select
              options={partnerOptions}
              value={partnerOptions.find((o) => o.value === selectedPartner?.id) ?? null}
              onChange={(opt) => {
                const p = partners.find((p) => p.id === opt?.value) ?? null;
                setSelectedPartner(p);
              }}
              placeholder="Select campaign..."
              isClearable
              className="basic-select"
              classNamePrefix="select"
            />
          </div>

          {/* Partner cashback info */}
          {selectedPartner && (
            <div className="bg-purple-50 border border-purple-100 rounded-lg px-4 py-3 flex flex-wrap gap-4 text-sm">
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium self-center ${
                selectedPartner.cashbackType === "Fixed"
                  ? "bg-blue-100 text-blue-800"
                  : "bg-purple-100 text-purple-800"
              }`}>
                {selectedPartner.cashbackType}
              </span>
              <span className="text-gray-700">
                Total cashback: <strong>{formatCashback(selectedPartner, "totalCashback")}</strong>
              </span>
              <span className="text-gray-700">
                User cashback: <strong>{formatCashback(selectedPartner, "userCashback")}</strong>
              </span>
            </div>
          )}

          {/* CSV format hint */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 font-mono">
            <p className="font-semibold text-gray-700 mb-1 font-sans text-sm">Expected CSV format:</p>
            {hasFixedAmount ? (
              <>
                <p>userId</p>
                <p className="text-gray-400">user@example.com</p>
                <p className="text-gray-400">1a2b3c4d</p>
                <p className="mt-1 font-sans not-italic text-gray-500">
                  Amount is fixed at <strong>{selectedPartner?.fixedAmount} DKK</strong>. Date column is optional — defaults to today if omitted.
                </p>
              </>
            ) : (
              <>
                <p>userId, amount, transactionDate</p>
                <p className="text-gray-400">user@example.com, 250.00, 2026-03-01 14:30:00</p>
                <p className="text-gray-400">1a2b3c4d, 150.50</p>
                <p className="mt-1 font-sans not-italic text-gray-500">transactionDate is optional — defaults to today if omitted.</p>
              </>
            )}
            <p className="mt-1 font-sans not-italic text-gray-500">userId can be email or Oister User ID — auto-detected per row</p>
          </div>

          {/* Drop zone */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              isDragging ? "border-primary bg-purple-50" : "border-gray-300 hover:border-gray-400"
            }`}
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
            />
            {fileName ? (
              <p className="text-sm font-medium text-gray-700">{fileName}</p>
            ) : (
              <>
                <p className="text-sm text-gray-500">Drag & drop a CSV file here, or click to browse</p>
                <p className="text-xs text-gray-400 mt-1">.csv files only</p>
              </>
            )}
          </div>

          {parseError && (
            <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
              {parseError}
            </div>
          )}

          {/* Preview */}
          {rows.length > 0 && selectedPartner && (
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Preview — {rows.length} rows
                {invalidRows.length > 0 && (
                  <span className="text-red-500 ml-2">{invalidRows.length} invalid</span>
                )}
              </p>
              <div className="overflow-x-auto max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2 text-gray-600">User ID</th>
                      <th className="text-left px-4 py-2 text-gray-600">Type</th>
                      <th className="text-left px-4 py-2 text-gray-600">
                        {hasFixedAmount ? `Amount (fixed: ${selectedPartner?.fixedAmount} DKK)` : "Amount"}
                      </th>
                      <th className="text-left px-4 py-2 text-gray-600">Date</th>
                      <th className="text-left px-4 py-2 text-gray-600">Total Cashback</th>
                      <th className="text-left px-4 py-2 text-gray-600">User Cashback</th>
                      <th className="text-left px-4 py-2 text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, i) => {
                      const amount = hasFixedAmount ? selectedPartner!.fixedAmount! : parseAmount(row.amount);
                      const isInvalid = !row.userId || (!hasFixedAmount && amount === null);
                      const idType = row.userId ? detectUserIdType(row.userId) : null;
                      return (
                        <tr key={i} className={isInvalid ? "bg-red-50" : i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}>
                          <td className="px-4 py-1.5 text-gray-700">{row.userId || "—"}</td>
                          <td className="px-4 py-1.5">
                            {idType && (
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                idType === "Email"
                                  ? "bg-green-100 text-green-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}>
                                {idType === "Email" ? "Email" : "ID"}
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-1.5 text-gray-700">
                            {hasFixedAmount ? `${selectedPartner?.fixedAmount} DKK` : (row.amount || "—")}
                          </td>
                          <td className="px-4 py-1.5 text-gray-700">
                            {row.transactionDate || <span className="text-gray-400 italic">today</span>}
                          </td>
                          <td className="px-4 py-1.5 text-gray-600">
                            {amount !== null ? calcCashback(selectedPartner, amount, "totalCashback") : "—"}
                          </td>
                          <td className="px-4 py-1.5 text-gray-600">
                            {amount !== null ? calcCashback(selectedPartner, amount, "userCashback") : "—"}
                          </td>
                          <td className="px-4 py-1.5">
                            {isInvalid
                              ? <span className="text-red-500">Invalid</span>
                              : <span className="text-green-600">OK</span>
                            }
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex gap-3 justify-end pt-2">
            <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} isLoading={isSubmitting} disabled={!canUpload}>
              Upload {canUpload ? `(${rows.length} rows)` : ""}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UploadReportModal;
